import React, { useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useApplyCartDiscountMutation } from '../services/purchase.endpoints';

interface DiscountCodeProps {
  onDiscountUpdate: (summary: any) => void; // Replace 'any' with 'CartPriceSummary | null' if CartPriceSummary is imported
}

const DiscountCode: React.FC<DiscountCodeProps> = ({ onDiscountUpdate }) => {
  const [discountCode, setDiscountCode] = useState('');
  const [feedback, setFeedback] = useState<{
    message: string;
    success: boolean;
  } | null>(null);

  const [applyDiscount, { isLoading }] = useApplyCartDiscountMutation();

  const handleApply = async () => {
    if (!discountCode.trim()) return;

    try {
      const response = await applyDiscount({
        discountCode: discountCode.trim(),
      }).unwrap();
      setFeedback({ message: response.message, success: response.success });
      onDiscountUpdate(response);
    } catch (error: any) {
      setFeedback({
        message: error?.data?.message || 'Failed to apply discount',
        success: false,
      });
      onDiscountUpdate(null);
    }
  };

  const handleClear = () => {
    setDiscountCode('');
    setFeedback(null);
    onDiscountUpdate(null);
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom sx={{ color: '#08c' }}>
        Have a Promo Code?
      </Typography>

      {feedback && (
        <Alert
          sx={{ mb: 2 }}
          severity={feedback.success ? 'success' : 'error'}
          action={
            <Tooltip title="Remove applied discount">
              <IconButton
                size="small"
                aria-label="Remove applied discount"
                onClick={handleClear}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          }
        >
          {feedback.message}
        </Alert>
      )}

      <Box display="flex" alignItems="center" gap={1}>
        <TextField
          label="Promo Code"
          placeholder="Enter promo code"
          value={discountCode}
          onChange={(e) => setDiscountCode(e.target.value)}
          name="discountCode"
          variant="outlined"
          margin="none"
          size="small" // reduces height
          fullWidth
          InputProps={{
            style: { height: 40 }, // matches standard button height
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleApply}
          size="medium" // consistent height with default MUI buttons
          disabled={!discountCode.trim() || isLoading}
          sx={{ minWidth: '80px', height: 40 }} // height match and compact width
        >
          Apply
        </Button>
      </Box>
    </Box>
  );
};

export default DiscountCode;
